import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Target, DollarSign, 
  Eye, Users, MousePointer, BarChart3, PieChart, Filter,
  MapPin, Calendar, Settings, Download
} from 'lucide-react';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';

const ChannelDetails = () => {
  const { channelName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedAdType, setSelectedAdType] = useState('all');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const normalizedChannelName = location.state?.channelName || (channelName ? channelName.charAt(0).toUpperCase() + channelName.slice(1) : 'Digital');

  // Mock detailed data for the channel
  const channelData = location.state?.data || {
    name: normalizedChannelName,
    planned: 6000000,
    actual: 6850000,
    percentage: 38.5,
    performance: 114.2,
    growth: 28.3,
    status: 'exceeding'
  };

  const detailedMetrics = {
    Digital: {
      platforms: ['Google Ads', 'Meta', 'TikTok', 'LinkedIn', 'Twitter'],
      adTypes: ['Search', 'Display', 'Video', 'Social', 'Programmatic'],
      industryMetrics: {
        primaryKPIs: ['ROAS', 'CPA', 'CTR', 'Impressions'],
        successMetrics: ['Conversion Rate', 'Cost per Click', 'Quality Score', 'Attribution']
      },
      regions: [
        { name: 'UAE', performance: 118.5, spend: 2450000, improvement: '+15.2%', cpa: 42.30, roas: 4.8, ctr: 3.6, impressions: 24500000, incrementalLift: '+28%' },
        { name: 'Saudi Arabia', performance: 108.2, spend: 2100000, improvement: '+8.7%', cpa: 48.90, roas: 3.9, ctr: 3.2, impressions: 21000000, incrementalLift: '+22%' },
        { name: 'Kuwait', performance: 95.3, spend: 850000, improvement: '+3.1%', cpa: 52.10, roas: 3.2, ctr: 2.8, impressions: 8500000, incrementalLift: '+12%' },
        { name: 'Qatar', performance: 112.8, spend: 920000, improvement: '+12.8%', cpa: 38.70, roas: 4.2, ctr: 3.8, impressions: 9200000, incrementalLift: '+25%' },
        { name: 'Bahrain', performance: 125.4, spend: 530000, improvement: '+22.4%', cpa: 35.60, roas: 5.1, ctr: 4.1, impressions: 5300000, incrementalLift: '+34%' }
      ],
      cityData: [
        { city: 'Dubai', country: 'UAE', performance: 124.2, spend: 1200000, cpa: 38.50, roas: 5.2, ctr: 3.8, impressions: 12000000 },
        { city: 'Abu Dhabi', country: 'UAE', spend: 850000, performance: 115.8, cpa: 44.20, roas: 4.6, ctr: 3.4, impressions: 8500000 },
        { city: 'Riyadh', country: 'Saudi Arabia', spend: 950000, performance: 110.3, cpa: 46.80, roas: 4.1, ctr: 3.2, impressions: 9500000 },
        { city: 'Jeddah', country: 'Saudi Arabia', spend: 720000, performance: 105.1, cpa: 52.30, roas: 3.8, ctr: 2.9, impressions: 7200000 },
        { city: 'Kuwait City', country: 'Kuwait', spend: 650000, performance: 98.7, cpa: 49.90, roas: 3.4, ctr: 2.8, impressions: 6500000 }
      ],
      improvements: [
        { metric: 'Click-Through Rate (CTR)', before: 2.8, after: 3.6, improvement: '+28.6%', period: 'vs Last Quarter', incrementalImpact: '+$450K revenue' },
        { metric: 'Conversion Rate', before: 1.2, after: 1.8, improvement: '+50.0%', period: 'vs Last Quarter', incrementalImpact: '+$680K revenue' },
        { metric: 'Cost per Acquisition (CPA)', before: 58.40, after: 45.20, improvement: '-22.6%', period: 'vs Last Quarter', incrementalImpact: 'Saved $320K' },
        { metric: 'Return on Ad Spend (ROAS)', before: 3.1, after: 4.1, improvement: '+32.3%', period: 'vs Last Quarter', incrementalImpact: '+$1.2M revenue' }
      ]
    },
    TV: {
      platforms: ['Satellite', 'Cable', 'Streaming', 'Local Broadcast'],
      adTypes: ['Prime Time', 'Day Time', 'Sports', 'News', 'Entertainment'],
      industryMetrics: {
        primaryKPIs: ['GRP', 'TRP', 'Prime Time %', 'Reach'],
        successMetrics: ['Brand Awareness', 'Frequency', 'Cost per GRP', 'Share of Voice']
      },
      regions: [
        { name: 'Saudi Arabia', performance: 102.5, spend: 2200000, improvement: '+12.5%', grp: 145, trp: 128, primeTimeShare: 68, reach: 4.2, incrementalLift: '+18%' },
        { name: 'UAE', performance: 88.7, spend: 1800000, improvement: '+5.2%', grp: 118, trp: 102, primeTimeShare: 52, reach: 3.8, incrementalLift: '+14%' },
        { name: 'Kuwait', performance: 85.3, spend: 580000, improvement: '+2.1%', grp: 85, trp: 78, primeTimeShare: 45, reach: 2.1, incrementalLift: '+8%' },
        { name: 'Qatar', performance: 95.8, spend: 370000, improvement: '+8.9%', grp: 92, trp: 85, primeTimeShare: 58, reach: 2.8, incrementalLift: '+12%' }
      ],
      improvements: [
        { metric: 'Gross Rating Points (GRP)', before: 98, after: 145, improvement: '+48.0%', period: 'vs Last Campaign', incrementalImpact: '+$280K brand value' },
        { metric: 'Target Rating Points (TRP)', before: 85, after: 128, improvement: '+50.6%', period: 'vs Last Campaign', incrementalImpact: '+$420K efficiency' },
        { metric: 'Prime Time Share', before: 42, after: 68, improvement: '+61.9%', period: 'vs Last Campaign', incrementalImpact: '+$350K premium value' },
        { metric: 'Reach (Million)', before: 3.2, after: 4.1, improvement: '+28.1%', period: 'vs Last Campaign', incrementalImpact: '+900K new audience' }
      ]
    },
    Outdoor: {
      platforms: ['Billboards', 'Digital OOH', 'Transit', 'Street Furniture'],
      adTypes: ['Highway', 'Shopping Malls', 'Airports', 'Metro', 'Bus Stops'],
      industryMetrics: {
        primaryKPIs: ['Daily Traffic', 'Locations', 'Visibility %', 'Impressions'],
        successMetrics: ['Cost per Thousand Impressions', 'Dwell Time', 'Recall Rate', 'Brand Lift']
      },
      regions: [
        { name: 'UAE', performance: 118.5, spend: 1450000, improvement: '+15.2%', dailyTraffic: 185000, locations: 42, visibility: 87, impressions: 12800000, incrementalLift: '+32%' },
        { name: 'Saudi Arabia', performance: 108.2, spend: 1200000, improvement: '+8.7%', dailyTraffic: 142000, locations: 38, visibility: 82, impressions: 9800000, incrementalLift: '+24%' },
        { name: 'Kuwait', performance: 95.3, spend: 450000, improvement: '+3.1%', dailyTraffic: 68000, locations: 18, visibility: 75, impressions: 4200000, incrementalLift: '+18%' },
        { name: 'Qatar', performance: 112.8, spend: 520000, improvement: '+12.8%', dailyTraffic: 78000, locations: 22, visibility: 85, impressions: 5100000, incrementalLift: '+28%' }
      ],
      improvements: [
        { metric: 'Daily Traffic (Thousands)', before: 142, after: 185, improvement: '+30.3%', period: 'vs Last Campaign', incrementalImpact: '+$380K reach value' },
        { metric: 'Active Locations', before: 28, after: 42, improvement: '+50.0%', period: 'vs Last Campaign', incrementalImpact: '+$520K coverage' },
        { metric: 'Visibility Score', before: 68, after: 87, improvement: '+27.9%', period: 'vs Last Campaign', incrementalImpact: '+$290K impact' },
        { metric: 'Monthly Impressions (M)', before: 8.5, after: 12.8, improvement: '+50.6%', period: 'vs Last Campaign', incrementalImpact: '+$680K exposure' }
      ]
    },
    Radio: {
      platforms: ['FM', 'AM', 'Digital Radio', 'Podcasts'],
      adTypes: ['Drive Time', 'Weekend', 'Midday', 'Evening', 'Sponsorships'],
      industryMetrics: {
        primaryKPIs: ['Listeners (M)', 'Share %', 'Prime Time %', 'Spots per Day'],
        successMetrics: ['Recall Rate', 'Brand Mention', 'Time Spent Listening', 'Audience Quality']
      },
      regions: [
        { name: 'UAE', performance: 108.5, spend: 780000, improvement: '+12.2%', listeners: 2.1, marketShare: 12.4, primeTime: 68, spotsPerDay: 3.5, incrementalLift: '+16%' },
        { name: 'Saudi Arabia', performance: 95.2, spend: 650000, improvement: '+6.7%', listeners: 3.8, marketShare: 15.2, primeTime: 52, spotsPerDay: 4.2, incrementalLift: '+12%' },
        { name: 'Kuwait', performance: 88.3, spend: 190000, improvement: '+2.1%', listeners: 0.8, marketShare: 8.9, primeTime: 45, spotsPerDay: 2.8, incrementalLift: '+8%' }
      ],
      improvements: [
        { metric: 'Listeners (Million)', before: 1.8, after: 2.1, improvement: '+16.7%', period: 'vs Last Campaign', incrementalImpact: '+300K new listeners' },
        { metric: 'Market Share', before: 9.8, after: 12.4, improvement: '+26.5%', period: 'vs Last Campaign', incrementalImpact: '+$180K value' },
        { metric: 'Prime Time Share', before: 52, after: 68, improvement: '+30.8%', period: 'vs Last Campaign', incrementalImpact: '+$240K premium' },
        { metric: 'Average Spots per Day', before: 2.8, after: 3.5, improvement: '+25.0%', period: 'vs Last Campaign', incrementalImpact: '+$120K frequency' }
      ]
    },
    Print: {
      platforms: ['Newspapers', 'Magazines', 'Trade Publications', 'Directories'],
      adTypes: ['Full Page', 'Half Page', 'Quarter Page', 'Insert', 'Classifieds'],
      industryMetrics: {
        primaryKPIs: ['Readership (M)', 'Circulation', 'CPM', 'ROAS'],
        successMetrics: ['Pass-along Rate', 'Time Spent Reading', 'Brand Recall', 'Response Rate']
      },
      regions: [
        { name: 'UAE', performance: 92.5, spend: 420000, improvement: '+4.2%', readership: 1.2, circulation: 850000, cpm: 85.40, roas: 1.8, incrementalLift: '+6%' },
        { name: 'Saudi Arabia', performance: 88.2, spend: 380000, improvement: '+2.7%', readership: 2.1, circulation: 1200000, cpm: 78.90, roas: 1.9, incrementalLift: '+5%' },
        { name: 'Kuwait', performance: 85.3, spend: 180000, improvement: '+1.1%', readership: 0.6, circulation: 420000, cpm: 92.30, roas: 1.6, incrementalLift: '+3%' }
      ],
      improvements: [
        { metric: 'Readership (Million)', before: 1.1, after: 1.2, improvement: '+9.1%', period: 'vs Last Campaign', incrementalImpact: '+100K readers' },
        { metric: 'Circulation', before: 780000, after: 850000, improvement: '+9.0%', period: 'vs Last Campaign', incrementalImpact: '+70K circulation' },
        { metric: 'Cost per Mille (CPM)', before: 92.30, after: 85.40, improvement: '-7.5%', period: 'vs Last Campaign', incrementalImpact: 'Saved $28K' },
        { metric: 'Return on Ad Spend (ROAS)', before: 1.6, after: 1.8, improvement: '+12.5%', period: 'vs Last Campaign', incrementalImpact: '+$48K revenue' }
      ]
    }
  };

  const currentChannelData = detailedMetrics[normalizedChannelName] || detailedMetrics.Digital;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPerformanceBadge = (performance) => {
    if (performance > 110) return 'bg-emerald-100 text-emerald-800';
    if (performance > 100) return 'bg-blue-100 text-blue-800';
    if (performance > 90) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getImprovementColor = (improvement) => {
    if (improvement.startsWith('+')) return 'text-emerald-600';
    if (improvement.startsWith('-') && (improvement.includes('Cost') || improvement.includes('CPA'))) return 'text-emerald-600';
    return 'text-red-600';
  };

  const formatMillions = (value) => {
    if (typeof value !== 'number') return value;
    return `${(value / 1_000_000).toFixed(1)}M`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Dashboard</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                {normalizedChannelName} Channel Analytics
              </h1>
              <p className="text-gray-600">Comprehensive performance analysis with regional insights and optimization opportunities</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-l-4 border-l-emerald-500">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="h-5 w-5 mr-2 text-emerald-600" />
              Analysis Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger>
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="UAE">UAE</SelectItem>
                  <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                  <SelectItem value="Kuwait">Kuwait</SelectItem>
                  <SelectItem value="Qatar">Qatar</SelectItem>
                  <SelectItem value="Bahrain">Bahrain</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger>
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  <SelectItem value="Dubai">Dubai</SelectItem>
                  <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                  <SelectItem value="Riyadh">Riyadh</SelectItem>
                  <SelectItem value="Jeddah">Jeddah</SelectItem>
                  <SelectItem value="Kuwait City">Kuwait City</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedAdType} onValueChange={setSelectedAdType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Ad Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ad Types</SelectItem>
                  {currentChannelData.adTypes.map((type, index) => (
                    <SelectItem key={index} value={type.toLowerCase()}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger>
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {currentChannelData.platforms.map((platform, index) => (
                    <SelectItem key={index} value={platform.toLowerCase()}>{platform}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <BarChart3 className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Spend</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(channelData.actual)}</p>
                  <p className="text-xs text-emerald-600">vs {formatCurrency(channelData.planned)} planned</p>
                </div>
                <DollarSign className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Performance</p>
                  <p className="text-2xl font-bold text-blue-600">{channelData.performance.toFixed(1)}%</p>
                  <p className="text-xs text-blue-600">vs plan target</p>
                </div>
                <Target className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Growth Rate</p>
                  <p className="text-2xl font-bold text-purple-600">+{channelData.growth.toFixed(1)}%</p>
                  <p className="text-xs text-purple-600">YoY improvement</p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Market Share</p>
                  <p className="text-2xl font-bold text-orange-600">{channelData.percentage.toFixed(1)}%</p>
                  <p className="text-xs text-orange-600">of total budget</p>
                </div>
                <PieChart className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="regional" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="regional">Regional Performance</TabsTrigger>
            <TabsTrigger value="city">City Analysis</TabsTrigger>
            <TabsTrigger value="improvements">Performance Improvements</TabsTrigger>
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
          </TabsList>

          <TabsContent value="regional" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentChannelData.regions.map((region, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{region.name}</CardTitle>
                      <Badge className={getPerformanceBadge(region.performance)}>
                        {region.performance.toFixed(1)}%
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Spend:</span>
                        <span className="font-semibold">{formatCurrency(region.spend)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Improvement:</span>
                        <span className={`font-semibold ${getImprovementColor(region.improvement)}`}>
                          {region.improvement}
                        </span>
                      </div>

                      {/* Channel-specific KPIs */}
                      {normalizedChannelName === 'Digital' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">CPA:</span>
                            <span className="font-semibold">${region.cpa.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">ROAS:</span>
                            <span className="font-semibold text-emerald-600">{region.roas.toFixed(1)}x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">CTR:</span>
                            <span className="font-semibold">{region.ctr.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Impressions:</span>
                            <span className="font-semibold">{formatMillions(region.impressions)}</span>
                          </div>
                        </div>
                      )}

                      {normalizedChannelName === 'TV' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">GRP:</span>
                            <span className="font-semibold">{region.grp}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">TRP:</span>
                            <span className="font-semibold text-blue-600">{region.trp}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Reach:</span>
                            <span className="font-semibold">{region.reach.toFixed(1)}M</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Prime Time %:</span>
                            <span className="font-semibold">{region.primeTimeShare}%</span>
                          </div>
                        </div>
                      )}

                      {normalizedChannelName === 'Outdoor' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Daily Traffic:</span>
                            <span className="font-semibold">{(region.dailyTraffic / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Locations:</span>
                            <span className="font-semibold">{region.locations}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Visibility:</span>
                            <span className="font-semibold">{region.visibility}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Impressions:</span>
                            <span className="font-semibold">{formatMillions(region.impressions)}</span>
                          </div>
                        </div>
                      )}

                      {normalizedChannelName === 'Radio' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Listeners:</span>
                            <span className="font-semibold">{region.listeners.toFixed(1)}M</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Market Share:</span>
                            <span className="font-semibold">{region.marketShare}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Prime Time %:</span>
                            <span className="font-semibold">{region.primeTime}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Spots/Day:</span>
                            <span className="font-semibold">{region.spotsPerDay.toFixed(1)}</span>
                          </div>
                        </div>
                      )}

                      {normalizedChannelName === 'Print' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Readership:</span>
                            <span className="font-semibold">{region.readership.toFixed(1)}M</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Circulation:</span>
                            <span className="font-semibold">{region.circulation.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">CPM:</span>
                            <span className="font-semibold">${region.cpm.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">ROAS:</span>
                            <span className="font-semibold text-emerald-600">{region.roas.toFixed(1)}x</span>
                          </div>
                        </div>
                      )}

                      {/* Performance Bar */}
                      <div className="pt-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Performance vs Target</span>
                          <span>{region.performance.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              region.performance > 110 ? 'bg-emerald-500' :
                              region.performance > 100 ? 'bg-blue-500' :
                              region.performance > 90 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(region.performance, 120)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="city" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-emerald-600" />
                  City-Level Performance Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-3 font-semibold text-gray-700">City</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Country</th>
                        <th className="text-right p-3 font-semibold text-gray-700">Spend</th>
                        <th className="text-right p-3 font-semibold text-gray-700">Performance</th>
                        <th className="text-right p-3 font-semibold text-gray-700">CPA</th>
                        <th className="text-right p-3 font-semibold text-gray-700">ROAS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentChannelData.cityData?.map((city, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-900">{city.city}</td>
                          <td className="p-3 text-gray-600">{city.country}</td>
                          <td className="p-3 text-right font-semibold">{formatCurrency(city.spend)}</td>
                          <td className="p-3 text-right">
                            <Badge className={getPerformanceBadge(city.performance)}>
                              {city.performance.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="p-3 text-right">${city.cpa.toFixed(2)}</td>
                          <td className="p-3 text-right font-semibold text-emerald-600">{city.roas.toFixed(1)}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="improvements" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentChannelData.improvements.map((improvement, index) => (
                <Card key={index} className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900">{improvement.metric}</h4>
                        <p className="text-sm text-gray-600">{improvement.period}</p>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-800">
                        {improvement.improvement}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Before:</span>
                        <span className="font-medium">
                          {improvement.metric.includes('Rate') || improvement.metric.includes('Awareness') ? 
                            `${improvement.before.toFixed(1)}%` : 
                            improvement.metric.includes('Cost') || improvement.metric.includes('CPA') ?
                            `$${improvement.before.toFixed(2)}` :
                            improvement.before.toFixed(1)
                          }
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">After:</span>
                        <span className="font-semibold text-emerald-600">
                          {improvement.metric.includes('Rate') || improvement.metric.includes('Awareness') ? 
                            `${improvement.after.toFixed(1)}%` : 
                            improvement.metric.includes('Cost') || improvement.metric.includes('CPA') ?
                            `$${improvement.after.toFixed(2)}` :
                            improvement.after.toFixed(1)
                          }
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="recommendations" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-l-4 border-l-emerald-500">
                <CardHeader>
                  <CardTitle className="text-emerald-700">High Priority Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="font-medium text-emerald-900 mb-2">Scale Top Performing Regions</div>
                    <div className="text-sm text-emerald-800 mb-3">
                      {channelName === 'Digital' ? 'UAE and Bahrain showing exceptional ROAS (5.1x+). Increase budget allocation by 25%.' : 'Saudi Arabia leading with strong performance. Consider expanding prime time slots.'}
                    </div>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      Implement Now
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="font-medium text-blue-900 mb-2">Optimize Underperforming Cities</div>
                    <div className="text-sm text-blue-800 mb-3">
                      Kuwait City showing lower performance. Consider audience refinement and creative optimization.
                    </div>
                    <Button size="sm" variant="outline" className="border-blue-600 text-blue-600">
                      Analyze Further
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader>
                  <CardTitle className="text-purple-700">Strategic Opportunities</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="font-medium text-purple-900 mb-2">Cross-Channel Synergy</div>
                    <div className="text-sm text-purple-800 mb-3">
                      Combine {channelName.toLowerCase()} with outdoor advertising for 22% lift in brand awareness.
                    </div>
                    <Button size="sm" variant="outline" className="border-purple-600 text-purple-600">
                      Plan Integration
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="font-medium text-orange-900 mb-2">Seasonal Optimization</div>
                    <div className="text-sm text-orange-800 mb-3">
                      Ramadan and summer periods show 30%+ higher engagement. Prepare advanced campaigns.
                    </div>
                    <Button size="sm" variant="outline" className="border-orange-600 text-orange-600">
                      Schedule Planning
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Export Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Download className="h-5 w-5 mr-2 text-gray-600" />
                  Export & Share Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button variant="outline" className="flex items-center justify-center">
                    <Download className="h-4 w-4 mr-2" />
                    Export to Excel
                  </Button>
                  <Button variant="outline" className="flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Create Dashboard
                  </Button>
                  <Button variant="outline" className="flex items-center justify-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* AI Insights Chatbot */}
      <AIInsightsChatbot />
    </div>
  );
};

export default ChannelDetails;